import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Trash2, Download, LogOut, Users, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const token = localStorage.getItem('admin_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchLeads = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers, params: q ? { search: q } : {} });
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, headers]);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchLeads();
  }, [token, navigate, fetchLeads]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLeads(search);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo lead?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setTotal((prev) => prev - 1);
    } catch (err) {
      alert('Errore durante l\'eliminazione');
    } finally {
      setDeleting(null);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export`, {
        headers,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_lexrecupero_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore durante l\'esportazione');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f7]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between h-[60px]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#0E47B0] rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="font-bold text-[#1a1a2e] text-lg hidden sm:inline">Admin Panel — Lex Recupero™</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-[#4a4a6a] hover:text-[#0E47B0] text-sm no-underline transition-colors">Sito</a>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-[#4a4a6a] hover:text-red-500 text-sm bg-transparent border-none cursor-pointer transition-colors">
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0E47B0]/10 rounded-lg flex items-center justify-center">
                <Users size={20} className="text-[#0E47B0]" />
              </div>
              <div>
                <p className="text-[#4a4a6a] text-xs font-medium">Richieste Totali</p>
                <p className="text-[#1a1a2e] text-2xl font-bold">{total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                <Users size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-[#4a4a6a] text-xs font-medium">Oggi</p>
                <p className="text-[#1a1a2e] text-2xl font-bold">
                  {leads.filter(l => {
                    try { return new Date(l.created_at).toDateString() === new Date().toDateString(); } catch { return false; }
                  }).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Users size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-[#4a4a6a] text-xs font-medium">Questa settimana</p>
                <p className="text-[#1a1a2e] text-2xl font-bold">
                  {leads.filter(l => {
                    try {
                      const d = new Date(l.created_at);
                      const now = new Date();
                      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                      return d >= weekAgo;
                    } catch { return false; }
                  }).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="p-4 sm:p-5 border-b border-gray-100 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 justify-between">
            <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 max-w-[400px]">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca per nome, email, telefono..."
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#0E47B0] transition-colors"
                />
              </div>
              <button type="submit" className="px-4 py-2.5 bg-[#0E47B0] text-white text-sm font-medium rounded-lg border-none cursor-pointer hover:bg-[#0d3d96] transition-colors">
                Cerca
              </button>
            </form>

            <div className="flex items-center gap-2">
              <button onClick={() => { setSearch(''); fetchLeads(); }} className="flex items-center gap-1.5 px-3 py-2.5 text-[#4a4a6a] hover:text-[#0E47B0] text-sm bg-transparent border border-gray-200 rounded-lg cursor-pointer transition-colors">
                <RefreshCw size={14} /> Aggiorna
              </button>
              <button onClick={handleExport} className="flex items-center gap-1.5 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg border-none cursor-pointer transition-colors">
                <Download size={14} /> Esporta CSV
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="py-20 text-center text-gray-400">Caricamento...</div>
            ) : leads.length === 0 ? (
              <div className="py-20 text-center text-gray-400">Nessuna richiesta trovata</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Telefono</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data richiesta</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-4">
                        <span className="text-sm font-medium text-[#1a1a2e]">{lead.first_name} {lead.last_name}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-[#4a4a6a]">{lead.email}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-[#4a4a6a]">{lead.phone || '—'}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-[#4a4a6a]">{formatDate(lead.created_at)}</span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => handleDelete(lead.id)}
                          disabled={deleting === lead.id}
                          className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer transition-colors disabled:opacity-40 p-1"
                          title="Elimina"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
